import { useEffect, useState } from "react";
import { useIsVerified } from "src/components/email-verification";
import {
  MainCard,
  MainCardContent,
  MainCardTitle,
} from "src/components/main-card-type.tsx";
import { Button } from "src/components/ui/button";
import {
  checkCalculatorUsageLimitAndRedirect,
  loadSavedCalculatorData,
  sanitizeBrlValue,
  saveCalculatorData,
} from "src/utils/functions";
import { calcularSeguroDesemprego } from "../utils/SeguroDesempregoFunctions";
import { 
  SeguroDesempregoInputData, 
  SeguroDesempregoResultsType, 
  ValidationMessages,
  REQUISITOS_TEMPO_TRABALHO,
  EXPLICACOES_CAMPOS 
} from "../calculadora-seguro-desemprego";

const CALCULATOR_KEY = "calculadora-seguro-desemprego";

const DEFAULT_VALUES: SeguroDesempregoInputData = {
  antepenultimoSalario: "",
  penultimoSalario: "",
  ultimoSalario: "",
  historicoRecebimento: 'nunca',
  mesesTrabalhados: "",
} as const;

const Tooltip = ({ children, text }: { children: React.ReactNode, text: string }) => (
  <div className="relative group">
    {children}
    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 w-80 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20 text-center">
      {text}
    </div>
  </div>
);

const parseNumericValue = (value: string): number => {
  return Number.parseFloat(value.replace(/[^0-9,-]+/g, "").replace(",", ".")) || 0;
};

export const SeguroDesempregoInputCard = ({
  setResults,
}: {
  setResults: (value: SeguroDesempregoResultsType | null) => void;
}) => {
  const [message, setMessage] = useState<string>("");
  const [formData, setFormData] = useState<SeguroDesempregoInputData>(DEFAULT_VALUES);
  const [isCalculating, setIsCalculating] = useState(false);

  const isVerified = useIsVerified();

  const updateFormField = <K extends keyof SeguroDesempregoInputData>(field: K, value: SeguroDesempregoInputData[K]) => {
    setFormData((prev: SeguroDesempregoInputData) => ({ ...prev, [field]: value }));
  };

  const validateSalarios = (): string | null => {
    if (!formData.antepenultimoSalario || !formData.penultimoSalario || !formData.ultimoSalario) {
      return ValidationMessages.SALARIOS_OBRIGATORIOS;
    }
    
    const antepenultimo = parseNumericValue(formData.antepenultimoSalario);
    const penultimo = parseNumericValue(formData.penultimoSalario);
    const ultimo = parseNumericValue(formData.ultimoSalario);
    
    if (antepenultimo <= 0 || penultimo <= 0 || ultimo <= 0) {
      return ValidationMessages.SALARIO_INVALIDO;
    }
    
    return null;
  };

  const validateMesesTrabalhados = (): string | null => {
    if (!formData.mesesTrabalhados) {
      return ValidationMessages.MESES_OBRIGATORIO;
    }
    
    const meses = parseInt(formData.mesesTrabalhados);
    if (meses <= 0) {
      return ValidationMessages.MESES_INVALIDO;
    }
    
    return null;
  };

  const validateForm = (): string | null => {
    return validateSalarios() || validateMesesTrabalhados();
  };

  const executeCalculation = async () => {
    try {
      const resultado = calcularSeguroDesemprego(
        formData.antepenultimoSalario,
        formData.penultimoSalario,
        formData.ultimoSalario,
        formData.historicoRecebimento,
        formData.mesesTrabalhados
      );

      setResults(resultado);
      setMessage("");
    } catch (error) {
      console.error('Erro no executeCalculation:', error);
      setMessage("Erro no cálculo: " + (error as Error).message);
      setResults(null);
    }
  };

  const calcular = async () => {
    const validationError = validateForm();
    if (validationError) {
      setMessage(validationError);
      return;
    }

    setIsCalculating(true);
    setMessage("");

    try {
      await executeCalculation();
    } catch (error) {
      console.error('Erro no cálculo:', error);
      setMessage((error as Error).message || ValidationMessages.ERRO_CALCULO);
      setResults(null);
    } finally {
      setIsCalculating(false);
    }
  };

  const clear = () => {
    setFormData(DEFAULT_VALUES);
    setResults(null);
    setMessage("");
  };

  const handleSave = () => {
    saveCalculatorData(CALCULATOR_KEY, {
      antepenultimoSalario: formData.antepenultimoSalario,
      penultimoSalario: formData.penultimoSalario,
      ultimoSalario: formData.ultimoSalario,
      historicoRecebimento: formData.historicoRecebimento,
      mesesTrabalhados: formData.mesesTrabalhados,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSave();
    checkCalculatorUsageLimitAndRedirect(calcular);
  };

  useEffect(() => {
    const savedData = loadSavedCalculatorData(CALCULATOR_KEY);
    if (savedData) {
      setFormData({
        antepenultimoSalario: (savedData.antepenultimoSalario as string) || DEFAULT_VALUES.antepenultimoSalario,
        penultimoSalario: (savedData.penultimoSalario as string) || DEFAULT_VALUES.penultimoSalario,
        ultimoSalario: (savedData.ultimoSalario as string) || DEFAULT_VALUES.ultimoSalario,
        historicoRecebimento: (savedData.historicoRecebimento as 'nunca' | 'uma_vez' | 'duas_ou_mais') || DEFAULT_VALUES.historicoRecebimento,
        mesesTrabalhados: (savedData.mesesTrabalhados as string) || DEFAULT_VALUES.mesesTrabalhados,
      });
    }
  }, []);

  // Requisitos atuais baseados no histórico selecionado
  const requisitosAtuais = REQUISITOS_TEMPO_TRABALHO[formData.historicoRecebimento];

  return (
    <MainCard>
      <MainCardTitle>Calculadora de Seguro-Desemprego</MainCardTitle>
      <form onSubmit={handleSubmit}>
        <MainCardContent>
          {/* Último salário - campo principal individual */}
          <div>
            <Tooltip text={EXPLICACOES_CAMPOS.ultimoSalario}>
              <label className="block mb-2 font-medium text-sm cursor-help" htmlFor="ultimo-salario">
                Último salário * ℹ️
              </label>
            </Tooltip>
            <div className="flex border">
              <span className="inline-flex items-center px-3 border-r text-sm bg-[#e9ecef]">
                R$
              </span>
              <input
                className="flex-1 p-2 min-w-[100px]"
                id="ultimo-salario"
                placeholder="0,00"
                type="text"
                value={formData.ultimoSalario}
                onChange={(event) => updateFormField('ultimoSalario', sanitizeBrlValue(event))}
              />
            </div>
          </div>

          {/* Grid 2 colunas - Penúltimo e Antepenúltimo */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Tooltip text={EXPLICACOES_CAMPOS.penultimoSalario}>
                <label className="block mb-2 font-medium text-sm cursor-help" htmlFor="penultimo-salario">
                  Penúltimo salário * ℹ️
                </label>
              </Tooltip>
              <div className="flex border">
                <span className="inline-flex items-center px-3 border-r text-sm bg-[#e9ecef]">
                  R$
                </span>
                <input
                  className="flex-1 p-2 min-w-[100px]"
                  id="penultimo-salario"
                  placeholder="0,00"
                  type="text"
                  value={formData.penultimoSalario}
                  onChange={(event) => updateFormField('penultimoSalario', sanitizeBrlValue(event))}
                />
              </div>
            </div>
            
            <div>
              <Tooltip text={EXPLICACOES_CAMPOS.antepenultimoSalario}>
                <label className="block mb-2 font-medium text-sm cursor-help" htmlFor="antepenultimo-salario">
                  Antepenúltimo salário * ℹ️
                </label>
              </Tooltip>
              <div className="flex border">
                <span className="inline-flex items-center px-3 border-r text-sm bg-[#e9ecef]">
                  R$
                </span>
                <input
                  className="flex-1 p-2 min-w-[100px]"
                  id="antepenultimo-salario"
                  placeholder="0,00"
                  type="text"
                  value={formData.antepenultimoSalario}
                  onChange={(event) => updateFormField('antepenultimoSalario', sanitizeBrlValue(event))}
                />
              </div>
            </div>
          </div>
        </MainCardContent>

        {/* Grid com 2 cards */}
        <div className="px-4 pb-4 pt-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            
            {/* Card 1: Histórico com Select */}
            <div className="bg-[#e9ecef] p-4 rounded border">
              <Tooltip text={EXPLICACOES_CAMPOS.historicoRecebimento}>
                <h3 className="text-sm font-semibold mb-3 text-gray-700 cursor-help">
                  Já recebeu o seguro-desemprego anteriormente? * ℹ️
                </h3>
              </Tooltip>
              
              <select
                className="w-full p-3 border rounded bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.historicoRecebimento}
                onChange={(e) => updateFormField('historicoRecebimento', e.target.value as 'nunca' | 'uma_vez' | 'duas_ou_mais')}
              >
                <option value="nunca">NUNCA - Primeira solicitação</option>
                <option value="uma_vez">UMA ÚNICA VEZ - Segunda solicitação</option>
                <option value="duas_ou_mais">DUAS OU MAIS VEZES - Terceira solicitação</option>
              </select>

              <div className="mt-3 p-3 bg-yellow-50 rounded border border-yellow-200">
                <p className="text-xs text-yellow-800">
                  <strong>Importante:</strong> O histórico de recebimento determina os requisitos mínimos de tempo trabalhado e influencia no número de parcelas.
                </p>
              </div>
            </div>

            {/* Card 2: Meses Trabalhados - INTERFACE CORRIGIDA */}
            <div className="bg-[#e9ecef] p-4 rounded border">
              <Tooltip text={EXPLICACOES_CAMPOS.mesesTrabalhados}>
                <h3 className="text-sm font-semibold mb-3 text-gray-700 cursor-help">
                  Quantos meses você trabalhou consecutivamente? * ℹ️
                </h3>
              </Tooltip>
              
              <div className="space-y-3">
                <div className="flex border rounded">
                  <span className="inline-flex items-center px-3 border-r text-sm bg-white">
                    Meses
                  </span>
                  <input
                    className="flex-1 p-2 rounded-r text-center font-medium"
                    id="meses-trabalhados"
                    placeholder="0"
                    type="number"
                    min="1"
                    max="99"
                    value={formData.mesesTrabalhados}
                    onChange={(event) => updateFormField('mesesTrabalhados', event.target.value)}
                  />
                </div>
                
                {/* Card informativo com requisitos mínimos */}
                <div className="bg-blue-50 p-3 rounded border border-blue-200">
                  <h4 className="text-xs font-semibold text-blue-800 mb-1">
                    {requisitosAtuais.descricao} - Requisito Mínimo
                  </h4>
                  <p className="text-xs text-blue-700 mb-1">
                    {requisitosAtuais.detalhamento}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Container dos botões */}
        <div className="px-4 pb-4">
          <div className="flex justify-between">
            <Button
              disabled={!isVerified || isCalculating}
              type="submit"
              variant="default"
              className="sm:w-[150px]"
            >
              {isCalculating ? (
                <>
                  <span className="animate-spin mr-2">⏳</span>
                  Calculando...
                </>
              ) : (
                "Calcular"
              )}
            </Button>
            
            <Button onClick={clear} variant="ghost" type="button">
              Limpar
            </Button>
          </div>
        </div>
      </form>
      
      <p className="text-red-800 text-center mt-2 font-bold">{message}</p>
    </MainCard>
  );
};